import { cn } from "@lib/cn";
import type * as React from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import type Button from "./button";
import Icon from "./icon";

const NAVIGATION_CLASS =
  "tw-rounded-md tw-bg-white tw-size-7 tw-flex tw-items-center tw-justify-center tw-shadow-calendar tw-cursor-pointer focus:tw-outline focus:tw-outline-2 focus:tw-outline-offset-2 focus:tw-outline-[#7b17cf33]";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "default",
  formatters,
  components,
  plain = false,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
  /** Drop the card chrome (outline, shadow, padding, white fill) when the
   * calendar already sits inside a card/popover, so it doesn't read as a
   * pop-up inside a pop-up. */
  plain?: boolean;
}) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "tw-group/calendar tw-[[data-slot=card-content]_&]:tw-bg-transparent tw-[[data-slot=popover-content]_&]:tw-bg-transparent tw-[--cell-size:--spacing(8)] tw-w-[250px]",
        plain
          ? "tw-bg-transparent"
          : "tw-rounded-xl tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-p-3 tw-shadow-md",
        String.raw`rtl:**:[.rdp-button\_next>svg]:tw-rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:tw-rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("tw-w-fit", defaultClassNames.root),
        months: cn(
          "tw-flex tw-gap-4 tw-flex-col tw-md:tw-flex-row tw-relative",
          defaultClassNames.months,
        ),
        month: cn(
          "tw-flex tw-flex-col tw-w-full tw-gap-4",
          defaultClassNames.month,
        ),
        nav: cn(
          "tw-flex tw-items-center tw-gap-1 tw-w-full tw-absolute tw-top-0 tw-inset-x-0 tw-justify-between tw-h-8",
          defaultClassNames.nav,
        ),
        button_previous: cn(
          "tw-size-(--cell-size) tw-aria-disabled:tw-opacity-50 tw-p-0 tw-select-none",
          defaultClassNames.button_previous,
          NAVIGATION_CLASS,
        ),
        button_next: cn(
          "tw-size-(--cell-size) tw-aria-disabled:tw-opacity-50 tw-p-0 tw-select-none",
          defaultClassNames.button_next,
          NAVIGATION_CLASS,
        ),
        month_caption: cn(
          "tw-font-sans tw-text-[13px]/normal tw-font-medium tw-text-blue tw-h-8 tw-flex tw-items-center tw-justify-center",
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          "tw-w-full tw-flex tw-items-center tw-text-sm tw-font-medium tw-justify-center tw-h-(--cell-size) tw-gap-1.5",
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          "tw-relative tw-has-focus:tw-border-ring tw-border tw-border-input tw-shadow-xs tw-has-focus:tw-ring-ring/50 tw-has-focus:tw-ring-[3px] tw-rounded-md",
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn(
          "tw-absolute tw-inset-0 tw-opacity-0",
          defaultClassNames.dropdown,
        ),
        caption_label: cn(
          "tw-select-none tw-font-medium",
          captionLayout === "label"
            ? "tw-text-sm"
            : "tw-rounded-md tw-pl-2 tw-pr-1 tw-flex tw-items-center tw-gap-1 tw-text-sm tw-h-8 [&>svg]:tw-text-muted-foreground [&>svg]:tw-size-3.5",
          defaultClassNames.caption_label,
        ),
        table: "tw-w-full tw-border-collapse",
        weekdays: cn("tw-flex", defaultClassNames.weekdays),
        weekday: cn(
          "tw-text-muted-foreground tw-rounded-md tw-flex-1 tw-font-normal tw-text-[0.8rem] tw-select-none tw-text-blue-500 tw-font-sans tw-text-xs tw-font-normal",
          defaultClassNames.weekday,
        ),
        week: cn("tw-flex tw-w-full tw-mt-2", defaultClassNames.week),
        week_number_header: cn(
          "tw-select-none tw-w-(--cell-size)",
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          "tw-text-[0.8rem] tw-select-none tw-text-muted-foreground",
          defaultClassNames.week_number,
        ),
        day: cn(
          "tw-font-sans tw-text-[13px]/normal tw-font-normal tw-relative tw-transition-colors tw-duration-200 [&_button]:tw-absolute [&_button]:tw-inset-0 tw-text-blue tw-h-8 [&[data-today=true]:not([data-selected=true])]:tw-bg-blue-50 [&_button]:!tw-outline-none hover:[&:not([data-disabled=true]):not([data-selected=true])]:tw-bg-primary-50 tw-rounded-md tw-flex tw-items-center tw-justify-center hover:[&:not([data-disabled=true])]:tw-cursor-pointer tw-relative tw-w-full tw-h-full tw-p-0 tw-text-center [&:first-child[data-selected=true]_button]:tw-rounded-l-md [&:last-child[data-selected=true]_button]:tw-rounded-r-md tw-group/day data-[selected=true]:tw-bg-primary data-[selected=true]:tw-text-white tw-aspect-square tw-select-none",
          defaultClassNames.day,
        ),

        range_start: cn(
          "tw-rounded-l-md !tw-rounded-r-none tw-bg-accent",
          defaultClassNames.range_start,
        ),
        range_middle: cn(
          "tw-rounded-none !tw-bg-primary-100 !tw-text-blue",
          defaultClassNames.range_middle,
        ),
        range_end: cn(
          "tw-rounded-r-md !tw-rounded-l-none tw-bg-accent",
          defaultClassNames.range_end,
        ),
        today: cn(
          "tw-bg-accent tw-text-accent-foreground tw-rounded-md tw-data-[selected=true]:tw-rounded-none",
          defaultClassNames.today,
        ),
        outside: cn(
          "tw-text-muted-foreground tw-aria-selected:tw-text-muted-foreground",
          defaultClassNames.outside,
        ),
        disabled: cn(
          "tw-text-muted-foreground tw-opacity-50",
          defaultClassNames.disabled,
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <Icon
                icon="ri-arrow-left-s-line"
                className={cn("tw-size-4 tw-text-blue-400", className)}
                {...props}
              />
            );
          }

          if (orientation === "right") {
            return (
              <Icon
                icon="ri-arrow-right-s-line"
                className={cn("tw-size-4 tw-text-blue-400", className)}
                {...props}
              />
            );
          }

          return (
            <Icon
              icon="ri-arrow-down-line"
              className={cn("tw-size-4", className)}
              {...props}
            />
          );
        },
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="tw-size-(--cell-size) tw-flex tw-items-center tw-justify-center tw-text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

export { Calendar };
